import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import cleanDeep from 'clean-deep';
import { addYears, format, parse, subYears } from 'date-fns';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { NotificationManager } from 'react-notifications';
import { connect } from 'react-redux';
import Select from 'react-select';
import { Actions } from '../../../store/actions/employeeActions';
import { handleRawDate } from '../../common/DateComponents';
import Spinner from '../../common/Spinner';
import { validateFloat } from '../../common/validation/Validators';

const attachmentTypeOptions = [
    { label: 'DEA', value: 'DEA', rates: [{ label: 'Standard Rate', value: null }, { label: 'Higher Rate', value: 'Higher' }], jurisdiction: ["UK"] },
    { label: 'CMG', value: 'CMG', jurisdiction: ["UK"] },
    { label: 'AEO', value: 'AEO', jurisdiction: ["UK"] },
    { label: 'CTAEO', value: 'CTAEO', rates: [{ label: 'Standard Rate', value: null }], jurisdiction: ["UK"] },
    { label: 'CAO', value: 'CAO', jurisdiction: ["UK"] },
    { label: 'EA', value: 'EA', jurisdiction: ["UK"] },
    { label: 'CMA', value: 'CMA', jurisdiction: ["UK"] },
    { label: 'Reducing Balance Loan', value: 'Reducing Balance Loan', jurisdiction: ["UK","MT"] },
]

const AttachmentOfEarningsModal = ({
    attachmentTypeOptions,
    attachmentEarning,
    closeModal,
    modalOpen,
    currency,
    saveItem,
    saving,
    disabled
}) => {

    const [data, setData] = useState({})

    const updateField = (field, value) => {
        if (field.includes('[')) field = _.setWith({}, field, value, Object)
        const newData = _.merge(data, { ...(typeof field == 'object' ? field : { [field]: value }) })
        setData({ ...newData })
    }

    const percentageOptions = [
        { label: currency, value: false },
        { label: '%', value: true },
    ]

    useEffect(() => {
        if (!modalOpen || saving) return
        const hasAttachmentEarning = Object.keys(cleanDeep({ ...attachmentEarning }))?.length > 0
        if (!hasAttachmentEarning) {
            setData({
                totalAmount: 0,
                paidBefore: 0,
                dateOfIssue: format(new Date(), 'yyyy-MM-dd'),
                type: attachmentTypeOptions[0]?.value,
                deduction: { value: 0, percentage: false },
                protectedEarnings: { value: 0, percentage: false },
            })
        } else {
            setData({ ...attachmentEarning })
        }
    }, [attachmentEarning, modalOpen])

    if (!modalOpen) return null


    const selectedAttachmentType = attachmentTypeOptions?.find(item => item.value == data.type)
    const attachmentRates = selectedAttachmentType?.rates

    const submit = () => {
        const submitData = { ...data}
        if (!submitData.reference || !submitData.dateOfIssue || !submitData.totalAmount || !submitData.type) {
            NotificationManager.error('Please fill all the required fields*', 'Saving Attachment of Earnings')
            return
        }
        if (attachmentRates) {
            submitData.deduction = undefined
        } else {
            submitData.attachmentRate = undefined
        }
        saveItem(submitData)
    }


    return (
        <div className="modal" tabIndex="-1" role="dialog" aria-hidden="true" style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}>
            <div className="modal-dialog" style={{ width: "60%" }}>
                <div className="modal-content">
                    <div className="block block-themed block-transparent remove-margin-b" style={{ padding: 0 }}>
                        <div className="block-header bg-primary-dark" style={{ padding: 20, position: 'sticky', top: 0, zIndex: 50 }}>
                            <ul className="block-options">
                                <li>
                                    <button type="button" onClick={closeModal}><XMarkIcon className="h-6 w-6 mr-3 text-white" /></button>
                                </li>
                            </ul>
                            <h3 className="block-title">{data?.id ? 'Edit' : 'Add'} Attachment of Earnings</h3>
                        </div>
                        <div className="block-content">
                            <div className='col-xs-12 form-container'>
                                <div className="col-xs-6">
                                    <div className='form-material'>
                                        <label>Reference*</label>
                                        <input
                                            className="form-control form-input"
                                            id="reference"
                                            name="reference"
                                            value={data.reference}
                                            onChange={(e) => {
                                                updateField(e.target.name, e.target.value)
                                            }}
                                            autoComplete='off'
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-6">
                                    <div className='form-material'>
                                        <label>Date of Issue*</label>
                                        <ReactDatePicker
                                            disabled={disabled}
                                            dateFormat="dd MMM yyyy"
                                            className="form-control form-input"
                                            wrapperClassName="full-width"
                                            minDate={subYears(new Date(), 10)}
                                            maxDate={addYears(new Date(), 10)}
                                            selected={data.dateOfIssue ? parse(data.dateOfIssue, 'yyyy-MM-dd', new Date()) : null}
                                            autoComplete="off"
                                            onChange={(dateOfIssue,) => {
                                                updateField('dateOfIssue', format(dateOfIssue, 'yyyy-MM-dd'))
                                            }}
                                            onChangeRaw={e => handleRawDate(e.target.value, (dateOfIssue) => {
                                                updateField('dateOfIssue', format(dateOfIssue, 'yyyy-MM-dd'))
                                            })}
                                            strictParsing
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='col-xs-12 form-container'>
                                <div className="col-xs-4">
                                    <div className='form-material' style={{ position: 'relative' }}>
                                        <label >Total Amount*</label>
                                        <div style={{ position: 'absolute', top: 8 }}>
                                            {currency}
                                        </div>
                                        <input
                                            style={{ paddingLeft: '1rem' }}
                                            className="form-control form-input"
                                            id="totalAmount"
                                            name="totalAmount"
                                            value={data.totalAmount}
                                            onChange={(e) => {
                                                updateField(e.target.name, validateFloat(e.target.value))
                                            }}
                                            autoComplete='off'
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4">
                                    <div className="form-material">
                                        <label htmlFor="type">Type*</label>
                                        <Select
                                            options={attachmentTypeOptions}
                                            value={attachmentTypeOptions?.find(item => item.value == data.type)}
                                            placeholder="Choose one"
                                            isDisabled={disabled}
                                            onChange={(selected) => {
                                                updateField('type', selected?.value)
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4">
                                    <div className='form-material' style={{ position: 'relative' }}>
                                        <label >Amount Paid Before Creation</label>
                                        <div style={{ position: 'absolute', top: 8 }}>
                                            {currency}
                                        </div>
                                        <input
                                            style={{ paddingLeft: '1rem' }}
                                            className="form-control form-input"
                                            id="paidBefore"
                                            name="paidBefore"
                                            value={data.paidBefore}
                                            onChange={(e) => {
                                                updateField(e.target.name, validateFloat(e.target.value))
                                            }}
                                            autoComplete='off'
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                            </div>
                            {!attachmentRates ?
                                <div className='col-xs-12 form-container'>
                                    <div className="col-xs-8">
                                        <div className='form-material'>
                                            <label>Deduction</label>
                                            <input
                                                className="form-control form-input"
                                                id="deduction[value]"
                                                name="deduction[value]"
                                                value={data.deduction?.['value']}
                                                onChange={(e) => {
                                                    updateField(e.target.name, validateFloat(e.target.value))
                                                }}
                                                autoComplete='off'
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-xs-4">
                                        <div className='form-material'>
                                            <Select
                                                options={percentageOptions}
                                                value={percentageOptions?.find(item => item.value == !!data.deduction?.['percentage'])}
                                                placeholder="Choose one"
                                                isDisabled={disabled}
                                                onChange={(selected) => {
                                                    updateField('deduction[percentage]', selected?.value)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                : null}
                        </div>
                        <div className='col-xs-12 form-container'>
                            <div className="col-xs-8">
                                <div className="form-material">
                                    <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                        <input
                                            id="priority"
                                            name="priority"
                                            checked={data.priority}
                                            onClick={(e) => updateField(e.target.name, !data.priority)}
                                            onChange={() => { }} //To Prevent Error Spamming in Console
                                            type="checkbox"
                                            disabled={disabled}
                                        />
                                        <span></span>
                                        <label htmlFor="material-text" style={{ marginBottom: 0 }}>
                                            Priority
                                        </label>
                                    </label>
                                    <div className="clearfix"></div>
                                </div>
                                <p className='help-block' style={{ marginBottom: 0 }}>Priority carries forward any unpaid amounts to the next pay date.</p>
                            </div>
                            {attachmentRates?.length > 1 ?
                                <div className="col-xs-4">
                                    <div className="form-material">
                                        <label htmlFor="type">Rate</label>
                                        <Select
                                            options={attachmentRates}
                                            value={attachmentRates?.find(item => item.value == data.attachmentRate)}
                                            placeholder="Choose one"
                                            isDisabled={disabled}
                                            onChange={(selected) => {
                                                updateField('attachmentRate', selected?.value || null)
                                            }}
                                        />
                                    </div>
                                </div>
                                : null}
                        </div>
                        <div className='col-xs-12 form-container'>
                            <div className="col-xs-8">
                                <div className='form-material'>
                                    <label>Protected Earnings</label>
                                    <input
                                        className="form-control form-input"
                                        id="protectedEarnings[value]"
                                        name="protectedEarnings[value]"
                                        value={data.protectedEarnings?.['value']}
                                        onChange={(e) => {
                                            updateField(e.target.name, validateFloat(e.target.value))
                                        }}
                                        autoComplete='off'
                                        disabled={disabled}
                                    />
                                </div>
                            </div>
                            <div className="col-xs-4">
                                <div className='form-material'>
                                    <Select
                                        options={percentageOptions}
                                        value={percentageOptions?.find(item => item.value == !!data.protectedEarnings?.['percentage'])}
                                        placeholder="Choose one"
                                        isDisabled={disabled}
                                        onChange={(selected) => {
                                            updateField('protectedEarnings[percentage]', selected?.value)
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='col-xs-12 form-container'>
                            <div className="col-xs-8">
                                <div className="form-material">
                                    <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                        <input
                                            id="deductFee"
                                            name="deductFee"
                                            checked={data.deductFee}
                                            onClick={(e) => updateField(e.target.name, !data[e.target.name])}
                                            onChange={() => { }} //To Prevent Error Spamming in Console
                                            type="checkbox"
                                            disabled={disabled}
                                        />
                                        <span></span>
                                        <label htmlFor="material-text" style={{ marginBottom: 0 }}>
                                            Deduct {currency}1 Administration Fee
                                        </label>
                                    </label>
                                    <div className="clearfix"></div>
                                </div>
                            </div>
                        </div>
                        <div className='col-xs-12 form-container'>
                            <button
                                className='btn btn-success pull-right'
                                disabled={saving || disabled}
                                onClick={() => { if (!saving) submit() }}
                            >
                                {saving ? <>
                                    <Spinner
                                        style={{ width: '1.5rem', height: '1.5rem', marginRight: 5 }}
                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                                    <div style={{ float: "left" }}>Saving...</div>
                                </> : <>
                                    <CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
                                    <div style={{ float: "left" }}>Save Attachment of Earnings</div>
                                </>}
                            </button>
                        </div>
                        <div className='clearfix' />
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {

    const preselectedType = state.employee?.attachmentEarnings?.preselectedType

    return {
        attachmentEarning: state.employee?.attachmentEarnings?.attachment,
        modalOpen: state.employee?.attachmentEarnings?.attachmentEarningsModal,
        saving: state.employee?.attachmentEarnings?.saving,
        currency: state.company?.settings?.currency,
        disabled: (state?.permissions?.my?.permissions?.people == "Read"),
        attachmentTypeOptions: preselectedType ? attachmentTypeOptions.filter((t) => t.value === preselectedType ) :  attachmentTypeOptions.filter((t) => t.jurisdiction.includes(state.company?.settings?.jurisdiction)),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeModal: () => dispatch(Actions.toggleAttachmentEarningsModal({ open: false })),
    saveItem: (item) => dispatch(Actions.saveLongTermAdjustments({ item }))
});

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentOfEarningsModal);
